import { Link } from "react-router-dom";
import { FolderDown, Lock, FileImage, FileText, FileArchive, Download, ArrowRight, ClipboardCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/motion";
import { ASSET_CATEGORIES } from "@/data/dealerAssets";

const FILE_ICONS = { image: FileImage, pdf: FileText, zip: FileArchive };

function AssetCard({ item, i }) {
  const Icon = FILE_ICONS[item.type] || FileArchive;
  return (
    <Reveal i={i % 3}>
      <div className="clip-corner flex h-full flex-col border border-girder bg-steel/40 p-5" data-testid={`asset-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
        {item.type === "image" ? (
          <div className="clip-corner-sm flex h-32 items-center justify-center border border-girder bg-obsidian p-3">
            <img src={item.file} alt={item.title} loading="lazy" className="max-h-full w-auto object-contain" />
          </div>
        ) : (
          <div className="clip-corner-sm flex h-32 items-center justify-center border border-girder bg-obsidian">
            <Icon className="h-10 w-10 text-slate-500" />
          </div>
        )}
        <h4 className="mt-4 font-display text-lg font-bold uppercase leading-tight text-slate-100">{item.title}</h4>
        {item.desc && <p className="mt-2 flex-1 text-sm text-slate-400">{item.desc}</p>}
        <div className="mt-4 flex items-center justify-between">
          <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{item.format}{item.size ? ` · ${item.size}` : ""}</span>
          <a href={item.file} download data-testid={`download-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className="clip-line-sm inline-flex items-center gap-2 px-4 py-2 font-mono text-[11px] font-bold uppercase tracking-widest text-hivis transition-colors hover:text-obsidian hover:[--cb-bg:#F59E0B] [--cb-line:#F59E0B]">
            <Download className="h-3.5 w-3.5" /> Download
          </a>
        </div>
      </div>
    </Reveal>
  );
}

export default function DealerAssetsPage() {
  const { dealer, checking } = useAuth();
  const categories = ASSET_CATEGORIES.filter((c) => c.items.length > 0);

  return (
    <div data-testid="dealer-assets-page">
      <PageHeader
        kicker="Dealer Support"
        title="Dealer assets"
        sub="Brand artwork, photography, and ready-to-use materials — free for every Banner Bar dealer. New assets are added as they're produced."
        image="/assets/img/w-vconfig-bg-sdi0655.webp"
      />
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-12 lg:py-20">
        {checking ? (
          <p className="font-mono text-sm uppercase tracking-widest text-slate-500">Loading...</p>
        ) : !dealer ? (
          <Reveal>
            <div className="clip-corner mx-auto max-w-xl border border-girder bg-obsidian p-8 text-center" data-testid="assets-login-prompt">
              <Lock className="mx-auto h-8 w-8 text-hivis" />
              <h3 className="mt-4 font-display text-2xl font-bold uppercase text-slate-100">Dealers only</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">The asset library is part of your dealer account. Log in to download logos, artwork, and marketing materials.</p>
              <Link to="/dealer-portal" data-testid="assets-login-cta" className="clip-corner mt-6 inline-flex items-center gap-2 bg-hivis px-8 py-3.5 font-display font-bold uppercase text-obsidian transition-colors hover:bg-hivis-hover">
                Dealer login <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        ) : dealer.profile_completed === false ? (
          <Reveal>
            <div className="clip-corner mx-auto max-w-xl border border-hivis/40 bg-obsidian p-8 text-center" data-testid="assets-profile-prompt">
              <ClipboardCheck className="mx-auto h-8 w-8 text-hivis" />
              <h3 className="mt-4 font-display text-2xl font-bold uppercase text-slate-100">One quick step first</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">Confirm your account details and the asset library unlocks.</p>
              <Link to="/dealer/profile" data-testid="assets-profile-cta" className="clip-corner mt-6 inline-flex items-center gap-2 bg-hivis px-8 py-3.5 font-display font-bold uppercase text-obsidian transition-colors hover:bg-hivis-hover">
                Confirm your details
              </Link>
            </div>
          </Reveal>
        ) : categories.length === 0 ? (
          <Reveal>
            <p className="font-mono text-sm uppercase tracking-widest text-slate-500" data-testid="assets-empty">The asset library is being stocked — check back shortly.</p>
          </Reveal>
        ) : (
          <>
            <Reveal>
              <p className="max-w-3xl text-sm leading-relaxed text-slate-400">
                Use these in your storefront, quotes, social posts, and print work. Questions about usage, or need a different format or size? Call <a href="tel:9706505173" className="text-hivis">970-650-5173</a> — we're glad to help.
              </p>
            </Reveal>
            {categories.map((cat) => (
              <div key={cat.id} className="mt-14" data-testid={`asset-category-${cat.id}`}>
                <Reveal>
                  <div className="flex items-center gap-3">
                    <FolderDown className="h-5 w-5 text-hivis" />
                    <h3 className="font-display text-2xl font-bold uppercase text-slate-100">{cat.name}</h3>
                  </div>
                  {cat.blurb && <p className="mt-2 text-sm text-slate-500">{cat.blurb}</p>}
                </Reveal>
                <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {cat.items.map((item, i) => <AssetCard key={item.title} item={item} i={i} />)}
                </div>
              </div>
            ))}
          </>
        )}
      </section>
    </div>
  );
}
